import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, CreditCard, Building2, Clock, CheckCircle, Copy, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Bank account information for wire transfer
const BANK_INFO = {
  bank_name: "Garanti Bankası",
  account_holder: "OliSeq Biyoteknoloji Ltd.",
  iban: "TR09 0006 2001 0050 0006 6624 41",
  swift: "TGBATRIS",
  reference: "Order number will be your reference"
};

const Checkout = () => {
  const navigate = useNavigate();
  const { cart, getTotal, clearCart } = useCart();
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [completedOrder, setCompletedOrder] = useState(null);

  const [paymentMethod, setPaymentMethod] = useState('iyzico');
  const [shippingInfo, setShippingInfo] = useState({
    full_name: user?.full_name || user?.name || '',
    email: user?.email || '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    country: ''
  });
  const [notes, setNotes] = useState('');

  const subtotal = getTotal();
  const tax = subtotal * 0.20;
  const total = subtotal + tax;

  const handleSubmit = async (e, payLater = false) => {
    e?.preventDefault();
    setLoading(true);

    // Validate required fields
    const requiredFields = ['full_name', 'email', 'phone', 'address', 'city', 'postal_code', 'country'];
    const missingFields = requiredFields.filter(field => !shippingInfo[field]);
    
    if (missingFields.length > 0) {
      toast.error('Please fill in all required shipping fields');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/orders`,
        {
          payment_method: payLater ? 'pay_later' : paymentMethod,
          shipping_info: shippingInfo,
          notes: payLater ? `${notes}\n\n[Ödeme Sonra Yapılacak / Payment to be made later]` : notes
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const createdOrder = response.data;

      // If iyzico selected (not pay-later), initialize hosted checkout and redirect
      if (!payLater && paymentMethod === 'iyzico') {
        try {
          const initRes = await axios.post(
            `${API_URL}/payments/iyzico/init`,
            { order_id: createdOrder.id },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const url = initRes.data?.payment_page_url;
          if (url) {
            toast.success('Redirecting to iyzico secure payment...');
            window.location.href = url;
            return;
          }
          toast.error('Payment URL missing from iyzico response');
        } catch (initErr) {
          console.error('Iyzico init error:', initErr);
          const detail = initErr.response?.data?.detail || '';
          if (initErr.response?.status === 503) {
            toast.error('iyzico ödeme entegrasyonu henüz yapılandırılmadı. Lütfen Wire Transfer veya "Continue Without Payment" seçeneğini kullanın.');
          } else {
            toast.error(`iyzico hatası: ${detail || 'Ödeme başlatılamadı'}`);
          }
          setLoading(false);
          return;
        }
      }

      if (!payLater && paymentMethod === 'paytr') {
        toast.error('PayTR entegrasyonu henüz aktif değil. Lütfen iyzico, Wire Transfer veya "Continue Without Payment" seçin.');
        setLoading(false);
        return;
      }

      setCompletedOrder(createdOrder);
      setOrderComplete(true);

      if (payLater) {
        toast.success('Order placed! Our team will contact you for payment.');
      } else {
        toast.success('Order placed successfully!');
      }
    } catch (error) {
      console.error('Order error:', error);
      toast.error(error.response?.data?.detail || 'Failed to place order');
    } finally {
      setLoading(false);
    }
  };

  const handleContinueWithoutPayment = () => {
    handleSubmit(null, true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  // Order Complete Screen
  if (orderComplete && completedOrder) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-2xl mx-auto px-4 py-16">
          <Card className="text-center">
            <CardContent className="pt-8 pb-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Order Confirmed!</h1>
              <p className="text-gray-600 mb-6">Thank you for your order</p>
              
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <p className="text-sm text-gray-500 mb-1">Order Number</p>
                <p className="text-2xl font-bold text-blue-600">{completedOrder.order_number}</p>
              </div>

              {completedOrder.payment_method === 'pay_later' && (
                <Alert className="bg-amber-50 border-amber-200 mb-6 text-left">
                  <AlertCircle className="h-5 w-5 text-amber-600" />
                  <AlertDescription className="text-amber-800 ml-2">
                    <strong>Ödeme Sonra Yapılacak:</strong> Siparişiniz alındı. Ekibimiz ödeme düzenlemesi için sizinle iletişime geçecektir.
                    <br />
                    <span className="text-sm">Your order has been received. Our team will contact you to arrange payment.</span>
                  </AlertDescription>
                </Alert>
              )}

              {completedOrder.payment_method === 'wire_transfer' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
                  <p className="font-semibold text-blue-900 mb-2">Wire Transfer Details:</p>
                  <p className="text-sm text-blue-800">Please transfer <strong>€{completedOrder.total?.toFixed(2)}</strong> to complete your order.</p>
                  <p className="text-sm text-blue-800 mt-2">Use order number <strong>{completedOrder.order_number}</strong> as reference.</p>
                </div>
              )}

              <div className="space-y-3">
                <Button 
                  className="w-full bg-blue-600 hover:bg-blue-700" 
                  onClick={() => navigate('/orders')}
                >
                  View My Orders
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => navigate('/')}
                >
                  Continue Shopping
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (cart.items.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate('/cart')}
            className="flex items-center space-x-2 text-gray-700 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Cart</span>
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Checkout</h1>

        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Forms */}
            <div className="lg:col-span-2 space-y-6">
              {/* Shipping Information */}
              <Card>
                <CardHeader>
                  <CardTitle>Shipping Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="full_name">Full Name *</Label>
                      <Input
                        id="full_name"
                        required
                        value={shippingInfo.full_name}
                        onChange={(e) => setShippingInfo({ ...shippingInfo, full_name: e.target.value })}
                        data-testid="shipping-fullname"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        required
                        value={shippingInfo.email}
                        onChange={(e) => setShippingInfo({ ...shippingInfo, email: e.target.value })}
                        data-testid="shipping-email"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone *</Label>
                      <Input
                        id="phone"
                        required
                        value={shippingInfo.phone}
                        onChange={(e) => setShippingInfo({ ...shippingInfo, phone: e.target.value })}
                        data-testid="shipping-phone"
                      />
                    </div>
                    <div>
                      <Label htmlFor="country">Country *</Label>
                      <Input
                        id="country"
                        required
                        value={shippingInfo.country}
                        onChange={(e) => setShippingInfo({ ...shippingInfo, country: e.target.value })}
                        data-testid="shipping-country"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="address">Address *</Label>
                      <Input
                        id="address"
                        required
                        value={shippingInfo.address}
                        onChange={(e) => setShippingInfo({ ...shippingInfo, address: e.target.value })}
                        data-testid="shipping-address"
                      />
                    </div>
                    <div>
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        required
                        value={shippingInfo.city}
                        onChange={(e) => setShippingInfo({ ...shippingInfo, city: e.target.value })}
                        data-testid="shipping-city"
                      />
                    </div>
                    <div>
                      <Label htmlFor="postal_code">Postal Code *</Label>
                      <Input
                        id="postal_code"
                        required
                        value={shippingInfo.postal_code}
                        onChange={(e) => setShippingInfo({ ...shippingInfo, postal_code: e.target.value })}
                        data-testid="shipping-postal"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Payment Method */}
              <Card>
                <CardHeader>
                  <CardTitle>Payment Method</CardTitle>
                  <CardDescription>Select your preferred payment option</CardDescription>
                </CardHeader>
                <CardContent>
                  <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                    <div className="space-y-3">
                      {/* iyzico */}
                      <div className={`flex items-center space-x-3 p-4 border-2 rounded-lg transition-colors ${paymentMethod === 'iyzico' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                        <RadioGroupItem value="iyzico" id="iyzico" data-testid="payment-iyzico" />
                        <Label htmlFor="iyzico" className="flex-1 cursor-pointer">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <CreditCard className="w-5 h-5 mr-3 text-blue-600" />
                              <div>
                                <span className="font-medium">iyzico</span>
                                <p className="text-sm text-gray-500">Credit/Debit Card Payment</p>
                              </div>
                            </div>
                            <img src="https://www.iyzico.com/assets/images/content/logo.svg" alt="iyzico" className="h-6" onError={(e) => e.target.style.display='none'} />
                          </div>
                        </Label>
                      </div>

                      {/* PayTR */}
                      <div className={`flex items-center space-x-3 p-4 border-2 rounded-lg transition-colors ${paymentMethod === 'paytr' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                        <RadioGroupItem value="paytr" id="paytr" data-testid="payment-paytr" />
                        <Label htmlFor="paytr" className="flex-1 cursor-pointer">
                          <div className="flex items-center">
                            <CreditCard className="w-5 h-5 mr-3 text-green-600" />
                            <div>
                              <span className="font-medium">PayTR</span>
                              <p className="text-sm text-gray-500">Credit/Debit Card & Bank Transfer</p>
                            </div>
                          </div>
                        </Label>
                      </div>

                      {/* Wire Transfer */}
                      <div className={`flex items-start space-x-3 p-4 border-2 rounded-lg transition-colors ${paymentMethod === 'wire_transfer' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                        <RadioGroupItem value="wire_transfer" id="wire_transfer" className="mt-1" data-testid="payment-wire" />
                        <Label htmlFor="wire_transfer" className="flex-1 cursor-pointer">
                          <div className="flex items-center mb-3">
                            <Building2 className="w-5 h-5 mr-3 text-purple-600" />
                            <div>
                              <span className="font-medium">Wire Transfer (Bank Transfer)</span>
                              <p className="text-sm text-gray-500">Direct bank transfer with IBAN</p>
                            </div>
                          </div>
                          
                          {paymentMethod === 'wire_transfer' && (
                            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mt-3 ml-8">
                              <p className="text-sm font-semibold text-purple-900 mb-3">Bank Account Details:</p>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-600">Bank:</span>
                                  <span className="font-medium">{BANK_INFO.bank_name}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-600">Account Holder:</span>
                                  <span className="font-medium">{BANK_INFO.account_holder}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-600">IBAN:</span>
                                  <div className="flex items-center gap-2">
                                    <span className="font-mono font-medium text-purple-700">{BANK_INFO.iban}</span>
                                    <Button 
                                      type="button" 
                                      variant="ghost" 
                                      size="sm"
                                      onClick={() => copyToClipboard(BANK_INFO.iban)}
                                    >
                                      <Copy className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-gray-600">SWIFT/BIC:</span>
                                  <span className="font-medium">{BANK_INFO.swift}</span>
                                </div>
                              </div>
                              <Alert className="mt-3 bg-white">
                                <AlertDescription className="text-xs text-gray-600">
                                  Please use your order number as the payment reference. Your order will be processed once payment is confirmed.
                                </AlertDescription>
                              </Alert>
                            </div>
                          )}
                        </Label>
                      </div>
                    </div>
                  </RadioGroup>
                </CardContent>
              </Card>

              {/* Additional Notes */}
              <Card>
                <CardHeader>
                  <CardTitle>Additional Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    placeholder="Any special instructions or notes..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={4}
                    data-testid="order-notes"
                  />
                </CardContent>
              </Card>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-sm p-6 sticky top-24">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Order Summary</h2>
                
                {/* Cart Items */}
                <div className="space-y-3 mb-6 max-h-60 overflow-y-auto">
                  {cart.items.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm p-2 bg-gray-50 rounded">
                      <div>
                        <span className="font-medium capitalize">{item.application?.replace('_', ' ')}</span>
                        <p className="text-gray-500 text-xs">{item.technology?.replace('_', ' ')}</p>
                      </div>
                      <div className="text-right">
                        <span className="font-medium">€{item.price?.toFixed(2)}</span>
                        <p className="text-gray-500 text-xs">x{item.quantity}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <Separator className="my-4" />

                {/* Totals */}
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal</span>
                    <span data-testid="checkout-subtotal">€{subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>VAT (20%)</span>
                    <span data-testid="checkout-tax">€{tax.toFixed(2)}</span>
                  </div>
                  <div className="border-t pt-3 flex justify-between text-lg font-bold text-gray-900">
                    <span>Total</span>
                    <span data-testid="checkout-total">€{total.toFixed(2)}</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <Button
                    type="submit"
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    disabled={loading}
                    data-testid="place-order-button"
                  >
                    {loading ? 'Processing...' : 'Place Order'}
                  </Button>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white px-2 text-gray-500">or</span>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full border-amber-300 text-amber-700 hover:bg-amber-50"
                    disabled={loading}
                    onClick={handleContinueWithoutPayment}
                    data-testid="continue-without-payment-button"
                  >
                    <Clock className="w-4 h-4 mr-2" />
                    Continue Without Payment
                  </Button>
                  
                  <p className="text-xs text-center text-gray-500">
                    Order will be saved and our team will contact you for payment arrangement.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </form>
      </main>
    </div>
  );
};

export default Checkout;
